import type { Workflow } from "@/stores/WorkflowStore";

import { WorkflowOutput, AgentConfigOutput } from "@webuild-ai/admin/models/components";
import {
  WorkflowConfig,
  AgentConfig,
  ScheduledConfig,
} from "@webuild-ai/agent-platform/models/components";

import { logger } from "@/telemetry";

// Helper function to determine if a workflow is specialized (not the default chat workflow)
export const isSpecializedWorkflow = (workflow: Workflow | null): boolean => {
  return workflow?.id !== "chat_agent";
};

/**
 * Ids of built-in workflows that exist for internal plumbing (the default
 * chat workflow and the Workflow Builder's build-agent generator) rather
 * than as a user-authored workflow. Callers that list or browse workflows
 * for selection should exclude these.
 */
export const SYSTEM_WORKFLOW_IDS = new Set(["chat_agent", "workflow_generator"]);

/**
 * Extracts the default prompt value from a workflow's input schema.
 * Returns null if no default value is defined.
 *
 * Schema example:
 * {
 *   "input": {
 *     "prompt": {
 *       "type": "string",
 *       "description": "Click Send to generate your report",
 *       "value": "Generate Report"  // ← This is the default value
 *     }
 *   }
 * }
 */
export const getWorkflowDefaultPrompt = (workflow: Workflow | null): string | null => {
  if (!workflow?.input) return null;

  // Check for prompt.value in the input schema
  const promptInput = workflow.input?.prompt;

  if (promptInput && typeof promptInput === "object" && "value" in promptInput) {
    return promptInput.value || null;
  }

  return null;
};

const convertAgentConfig = (adminAgent: AgentConfigOutput): AgentConfig => ({
  type: adminAgent.type,
  // Cast parameters - admin uses Array<any>, agent-platform uses Array<string>
  parameters: adminAgent.parameters as { [k: string]: string | number | Array<string> },
  triggers: adminAgent.triggers,
  userInteraction: adminAgent.userInteraction,
});

/**
 * Converts WorkflowOutput from admin API to WorkflowConfig for agent-platform API.
 *
 * This converter provides safe default values for the required fields.
 */
export const convertWorkflowOutputToConfig = (workflow: WorkflowOutput): WorkflowConfig => {
  // Convert agents
  const agents = Object.entries(workflow.agents).reduce(
    (acc, [key, agent]) => {
      acc[key] = convertAgentConfig(agent);

      return acc;
    },
    {} as { [k: string]: AgentConfig },
  );

  // Normalise the schedule. The two SDKs disagree about ScheduledConfig, and the gap
  // widened with admin 1.16.0. Admin now has
  // `{cron?: string | null, values?, timezone?, enabled?, entries?}`, whereas
  // agent-platform 1.4.0 still has only `{cron: string, values}` with both required.
  //
  // The source object is spread rather than rebuilt from `cron` and `values`, so
  // `timezone`, `enabled` and the new `entries` stay on the payload. Rebuilding it
  // field by field would strip exactly what the scheduler needs.
  //
  // `cron` is checked rather than asserted. A multi-entry schedule may leave the
  // shared cadence unset and let each entry carry its own, and the platform has no
  // `entries` concept at all, so such a definition simply cannot be expressed to it.
  // Forwarding a null cron under a cast would build a payload the platform's own
  // schema rejects, and defaulting it to an empty string would put a cadence on the
  // wire that the definition never held. Dropping the block and saying so is the only
  // honest option until the platform SDK catches up. No caller reaches this today,
  // because the sole one converts the `workflow_generator` system workflow, which
  // carries no schedule.
  const scheduledSource = workflow.scheduled;
  const scheduledCron = scheduledSource?.cron;
  let scheduled: ScheduledConfig | undefined;

  if (scheduledSource) {
    if (typeof scheduledCron === "string" && scheduledCron.length > 0) {
      scheduled = {
        ...scheduledSource,
        cron: scheduledCron,
        values: scheduledSource.values ?? {},
      };
    } else {
      logger.warn(
        "Dropping a schedule agent-platform cannot represent: no shared cron on the definition",
        { workflowId: workflow.id, hasEntries: Boolean(scheduledSource.entries?.length) },
      );
    }
  }

  return {
    name: workflow.name,
    id: workflow.id,
    description: workflow.description,
    chatManagerPrompt: workflow.chatManagerPrompt,
    scheduled,
    input: workflow.input,
    agents,
  };
};
